import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  Box,
  Chip,
  Typography,
  Alert,
  Paper,
  Divider,
} from "@mui/material";
import { BLOG_ARTICLES, getBlogArticleBySlug } from "../blog/articles";
import { RANKED_PRODUCTS_DATA } from "../blog/generated/rankedProductsData";
import { apiGet } from "../services/apiService";
import "../style/Blog.css";

const formatDate = (rawDate) =>
  new Date(rawDate).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const getCreatedDate = (article) => article.createdDate || article.date;

const getUpdatedDate = (article) => {
  return article.updatedDate ?? null;
};

const getAuthor = (article) => article.author || "The Picky Kitty";

const getLegacyImageSrc = (coverImage) => {
  if (!coverImage) return null;
  if (coverImage.startsWith("http") || coverImage.startsWith("/")) return coverImage;
  return `/blog/${coverImage}`;
};

const getArticleCoverDesktopSrc = (article) => {
  if (article?.imageKey) return `/blog/cover/${article.imageKey}`;
  return getLegacyImageSrc(article?.coverImage);
};

const getArticleCoverMobileSrc = (article) => {
  if (article?.imageKey) return `/blog/preview/${article.imageKey}`;
  return getLegacyImageSrc(article?.coverImage);
};

const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const formatThresholdPercent = (value) => {
  if (value === null || value === undefined) return "";
  const text = Number(value).toFixed(2).replace(/\.?0+$/, "");
  return `${text}%`;
};

const humanizeMetricField = (field) => {
  if (!field) return "Metric";
  return String(field)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (char) => char.toUpperCase());
};

const roundTo = (value, decimals = 6) => Number(value.toFixed(decimals));

const generateAutoBuckets = (values, step) => {
  const numericValues = values
    .filter((value) => value !== null && value !== undefined && Number.isFinite(Number(value)))
    .map((value) => Number(value));

  if (numericValues.length === 0 || !step || step <= 0) return [];

  const minValue = Math.min(...numericValues);
  const maxValue = Math.max(...numericValues);

  const firstThreshold = roundTo((Math.floor(minValue / step) + 1) * step);
  const lastStart = roundTo(Math.floor(maxValue / step) * step);

  const buckets = [{ maxExclusive: firstThreshold }];

  let lower = firstThreshold;
  while (lower < lastStart) {
    const upper = roundTo(lower + step);
    buckets.push({ minInclusive: lower, maxExclusive: upper });
    lower = upper;
  }

  buckets.push({ minInclusive: lastStart });
  return buckets;
};

const BlogArticle = () => {
  const { slug } = useParams();
  const article = getBlogArticleBySlug(slug || "");
  const rankedDataset = article?.articleType === "ranked_products"
    ? RANKED_PRODUCTS_DATA[article.slug]
    : null;
  const rankedEntries = rankedDataset?.products || [];
  const [rankedProductsBySourceId, setRankedProductsBySourceId] = useState({});
  const [rankedProductsLoading, setRankedProductsLoading] = useState(false);

  const rankedSourceIds = useMemo(() => (
    rankedEntries
      .map((entry) => entry?.sourceId)
      .filter((value, index, arr) => value && arr.indexOf(value) === index)
  ), [rankedEntries]);

  useEffect(() => {
    const loadRankedProductDetails = async () => {
      if (article?.articleType !== "ranked_products" || rankedSourceIds.length === 0) {
        setRankedProductsBySourceId({});
        setRankedProductsLoading(false);
        return;
      }

      try {
        setRankedProductsLoading(true);
        const params = encodeURIComponent(rankedSourceIds.join(","));
        const products = await apiGet(`api/products-by-source?source_ids=${params}`);
        const mapped = Array.isArray(products)
          ? products.reduce((acc, product) => {
              if (product?.sourceId) {
                acc[product.sourceId] = product;
              }
              return acc;
            }, {})
          : {};
        setRankedProductsBySourceId(mapped);
      } catch (error) {
        setRankedProductsBySourceId({});
      } finally {
        setRankedProductsLoading(false);
      }
    };

    loadRankedProductDetails();
  }, [article?.articleType, rankedSourceIds]);

  const rankedProducts = useMemo(
    () => rankedEntries
      .map((entry) => {
        const details = rankedProductsBySourceId[entry?.sourceId];
        if (!details) return null;
        return {
          ...details,
          sourceId: entry.sourceId,
          rank: entry.rank,
        };
      })
      .filter(Boolean),
    [rankedEntries, rankedProductsBySourceId]
  );

  if (!article) {
    return <Navigate to="/Blog" replace />;
  }

  const createdDate = getCreatedDate(article);
  const updatedDate = getUpdatedDate(article);
  const author = getAuthor(article);

  const getProductRoute = (product) => {
    return product?.id ? `/Product/${product.id}` : null;
  };

  const renderProductLink = (product, className, content) => {
    const route = getProductRoute(product);
    if (!route) {
      return <span className={className}>{content}</span>;
    }
    return (
      <Link to={route} className={className}>
        {content}
      </Link>
    );
  };

  const getMetricDisplay = (product, metricField, unit = "%") => {
    const value = product?.[metricField];
    if (value === null || value === undefined) return "N/A";

    const source = product?.nutrientSources?.[metricField];
    const suffix = source === "guaranteed_analysis" ? "*" : "";
    return `${Number(value).toFixed(2)}${unit}${suffix}`;
  };

  const isGuaranteedMetric = (product, metricField) =>
    product?.nutrientSources?.[metricField] === "guaranteed_analysis";

  const getProductDisplayName = (product) => {
    const parts = [product?.brand, product?.name]
      .filter((part) => part && String(part).trim().length > 0);
    return parts.join(" - ");
  };

  const normalizeListLikeText = (value) => {
    if (!value) return "N/A";
    if (Array.isArray(value)) {
      const filtered = value.filter(Boolean);
      return filtered.length > 0 ? filtered.join(", ") : "N/A";
    }
    return String(value) || "N/A";
  };

  const renderParagraphContent = (paragraph) => {
    if (typeof paragraph === "string") {
      return paragraph;
    }

    if (paragraph?.linkText) {
      return (
        <>
          {paragraph.text}
          <Link to={paragraph.linkTo} className="blog-inline-link">
            {paragraph.linkText}
          </Link>
          {paragraph.afterText}
        </>
      );
    }

    if (paragraph?.boldText) {
      return (
        <>
          {paragraph.text}
          <strong>{paragraph.boldText}</strong>
          {paragraph.afterText}
        </>
      );
    }

    return paragraph?.text || "";
  };

  const renderFixedCardFields = (product) => (
    <div className="blog-ranked-fixed-fields">
      <Typography variant="body2">Category: {product?.category || "N/A"}</Typography>
      <Typography variant="body2">Texture: {product?.texture || "N/A"}</Typography>
      <Typography variant="body2">Format: {normalizeListLikeText(product?.format)}</Typography>
      <Typography variant="body2">Proteins: {normalizeListLikeText(product?.proteins)}</Typography>
    </div>
  );

  const rankedParagraphConfig = article.rankedParagraphs || null;
  const rankedProductsConfig = article.rankedProductsConfig || {};
  const rankedProductsHeading = article.rankedProductsHeading || "Ranked products";
  const rankedProductsSubheading = article.rankedProductsSubheading || null;
  const metricField = rankedParagraphConfig?.metricField
    || rankedProductsConfig?.sortField
    || article?.rankedCardFields?.[0]?.field
    || null;
  const metricLabel = rankedParagraphConfig?.metricLabel || humanizeMetricField(metricField);
  const metricUnit = rankedParagraphConfig?.unit || "%";
  const autoBucketStep = rankedParagraphConfig?.autoBucketStep;
  const cardFields = Array.isArray(article.rankedCardFields) && article.rankedCardFields.length > 0
    ? article.rankedCardFields
    : [{ field: metricField, label: metricLabel, unit: metricUnit, primary: true }];

  const isInBucket = (value, bucket) => {
    if (value === null || value === undefined) return false;
    if (bucket.minInclusive !== undefined && value < bucket.minInclusive) return false;
    if (bucket.maxExclusive !== undefined && value >= bucket.maxExclusive) return false;
    return true;
  };

  const bucketsToUse = rankedParagraphConfig
    ? (Array.isArray(rankedParagraphConfig.buckets) && rankedParagraphConfig.buckets.length > 0
        ? rankedParagraphConfig.buckets
        : generateAutoBuckets(rankedProducts.map((product) => product?.[metricField]), autoBucketStep))
    : [];

  const rawRangedSections = rankedParagraphConfig
    ? bucketsToUse.map((bucket, index) => {
        const sectionId = `range-${index}-${slugify(bucket.label || `bucket-${index}`)}`;
        const products = rankedProducts.filter((product) =>
          isInBucket(product?.[metricField], bucket)
        );
        return {
          id: sectionId,
          label: bucket.label || "",
          minInclusive: bucket.minInclusive,
          maxExclusive: bucket.maxExclusive,
          products,
        };
      })
    : [];

  const rangedSections = rawRangedSections
    .filter((section) => section.products.length > 0)
    .map((section, index, allVisible) => {
      const isFirstVisible = index === 0;
      const isLastVisible = index === allVisible.length - 1;

      let displayLabel = section.label;

      if (isFirstVisible && section.maxExclusive !== undefined) {
        displayLabel = `Less than ${formatThresholdPercent(section.maxExclusive)}`;
      } else if (isLastVisible && section.minInclusive !== undefined && section.maxExclusive === undefined) {
        displayLabel = `${formatThresholdPercent(section.minInclusive)} and above`;
      } else if (!displayLabel) {
        if (section.minInclusive !== undefined && section.maxExclusive !== undefined) {
          displayLabel = `Less than ${formatThresholdPercent(section.maxExclusive)}`;
        } else if (section.maxExclusive !== undefined) {
          displayLabel = `Less than ${formatThresholdPercent(section.maxExclusive)}`;
        } else if (section.minInclusive !== undefined) {
          displayLabel = `${formatThresholdPercent(section.minInclusive)} and above`;
        } else {
          displayLabel = "All products";
        }
      }

      return {
        ...section,
        displayLabel,
      };
    });

  const renderCardMetrics = (product) => (
    <div className="blog-ranked-metrics">
      {cardFields.map((fieldConfig) => (
        <Typography
          key={`${product.sourceId || product.id || product.name}-${fieldConfig.field}`}
          variant="body2"
          className={fieldConfig.primary ? "blog-ranked-metric blog-ranked-metric-primary" : "blog-ranked-metric"}
        >
          {fieldConfig.primary ? <strong>{fieldConfig.label}: </strong> : `${fieldConfig.label}: `}
          <span
            style={{
              color: isGuaranteedMetric(product, fieldConfig.field) ? "blue" : "inherit",
              fontWeight: fieldConfig.primary ? 700 : 400,
            }}
          >
            {getMetricDisplay(product, fieldConfig.field, fieldConfig.unit || "%")}
          </span>
        </Typography>
      ))}
    </div>
  );

  return (
    <div className="blog-container">
      <Box className="blog-back-link-wrap">
        <Link to="/Blog" className="blog-back-link">
          ← Back
        </Link>
      </Box>

      <article className="blog-article">
        <Typography variant="h4" component="h1" className="blog-article-title">
          {article.title}
        </Typography>

        {getArticleCoverMobileSrc(article) ? (
          <picture>
            <source media="(min-width: 769px)" srcSet={getArticleCoverDesktopSrc(article) || ""} />
            <img
              src={getArticleCoverMobileSrc(article) || ""}
              alt={article.coverImageAlt || article.title}
              className="blog-article-cover-image"
              loading="lazy"
            />
          </picture>
        ) : null}

        <div className="blog-date-meta">
          <Typography variant="body2" className="blog-date">
            Written on {formatDate(createdDate)} by {author}
          </Typography>
          {updatedDate ? (
            <Typography variant="body2" className="blog-date">
              Updated on {formatDate(updatedDate)}
            </Typography>
          ) : null}
        </div>

        {article.sections.map((section) => (
          <section key={section.heading} className="blog-article-section">
            <Typography variant="h5" component="h2" className="blog-article-heading">
              {section.heading}
            </Typography>
            {section.paragraphs.map((paragraph) => (
              <Typography
                key={typeof paragraph === "string"
                  ? paragraph
                  : `${section.heading}-${paragraph.linkText || paragraph.boldText || paragraph.text}`}
                variant="body1"
                className={`blog-article-paragraph${paragraph?.isFormula ? " blog-article-formula" : ""}${paragraph?.isHighlight ? " blog-article-highlight" : ""}`}
              >
                {renderParagraphContent(paragraph)}
              </Typography>
            ))}
          </section>
        ))}

        {article.articleType === "ranked_products" && (
          <section className="blog-article-section">
            <Typography variant="h5" component="h2" className="blog-article-heading">
              {rankedProductsHeading}
            </Typography>
            {rankedProductsSubheading ? (
              <Typography variant="body2" className="blog-ranked-subheading">
                {rankedProductsSubheading}
              </Typography>
            ) : null}

            {!rankedDataset ? (
              <Alert severity="warning">
                No generated dataset found for this ranked article.
              </Alert>
            ) : rankedEntries.length === 0 ? (
              <Alert severity="info">
                No generated products yet for this list. Run `yarn generate:ranked-articles` to refresh.
              </Alert>
            ) : rankedProductsLoading ? (
              <div className="blog-ranked-loading">
                <Typography variant="body2" color="text.secondary">
                  Loading product details...
                </Typography>
              </div>
            ) : rankedProducts.length === 0 ? (
              <Alert severity="warning">
                Ranked products were generated, but no matching products were found in this environment.
              </Alert>
            ) : (
              <>
                {rangedSections.length > 0 ? (
                  <>
                    <div className="blog-ranked-toc">
                      <Typography variant="subtitle1" className="blog-ranked-toc-title">
                        Table of contents
                      </Typography>
                      <ul className="blog-ranked-toc-list">
                        {rangedSections.map((section) => (
                          <li key={section.id}>
                            <a href={`#${section.id}`} className="blog-inline-link">
                              {section.displayLabel}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {rangedSections.map((section) => (
                      <div key={section.id} id={section.id} className="blog-ranked-range-section">
                        <Typography variant="h6" className="blog-ranked-range-title">
                          {section.displayLabel}
                        </Typography>
                        {section.products.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No products in this range.
                          </Typography>
                        ) : (
                          <div className="blog-ranked-list">
                            {section.products.map((product) => (
                              <Paper
                                key={product.sourceId || `${product.name}-${product.rank}`}
                                className="blog-ranked-item"
                              >
                                <div className="blog-ranked-item-head">
                                  {product?.cloudinaryUrl ? (
                                    renderProductLink(
                                      product,
                                      "blog-ranked-image-link",
                                      <img
                                        src={product.cloudinaryUrl}
                                        alt={product.name}
                                        className="blog-ranked-image"
                                      />
                                    )
                                  ) : null}
                                  <div className="blog-ranked-item-main">
                                    <Box className="blog-ranked-topline">
                                      <Typography variant="h6" className="blog-ranked-name">
                                        {renderProductLink(
                                          product,
                                          "blog-inline-link",
                                          getProductDisplayName(product)
                                        )}
                                      </Typography>
                                    </Box>

                                    {product?.line ? (
                                      <Typography variant="body2" className="blog-ranked-meta">
                                        {product.line}
                                      </Typography>
                                    ) : null}

                                    {renderFixedCardFields(product)}
                                  </div>
                                </div>

                                <Divider sx={{ my: 1 }} />
                                {renderCardMetrics(product)}
                              </Paper>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="blog-ranked-list">
                    {rankedProducts.map((product) => (
                      <Paper
                        key={product.sourceId || `${product.name}-${product.rank}`}
                        className="blog-ranked-item"
                      >
                        <div className="blog-ranked-item-head">
                          {product?.cloudinaryUrl ? (
                            renderProductLink(
                              product,
                              "blog-ranked-image-link",
                              <img
                                src={product.cloudinaryUrl}
                                alt={product.name}
                                className="blog-ranked-image"
                              />
                            )
                          ) : null}
                          <div className="blog-ranked-item-main">
                            <Box className="blog-ranked-topline">
                              <Typography variant="h6" className="blog-ranked-name">
                                {renderProductLink(
                                  product,
                                  "blog-inline-link",
                                  getProductDisplayName(product)
                                )}
                              </Typography>
                            </Box>

                            {product?.line ? (
                              <Typography variant="body2" className="blog-ranked-meta">
                                {product.line}
                              </Typography>
                            ) : null}

                            {renderFixedCardFields(product)}
                          </div>
                        </div>

                        <Divider sx={{ my: 1 }} />
                        {renderCardMetrics(product)}
                      </Paper>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        )}
      </article>
    </div>
  );
};

export default BlogArticle;
